import { useEffect, useState } from 'react'
import { router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Link } from '@inertiajs/react'
import { getDateLocale } from '@/lib/i18n'
import { GlobeIcon, HeartPulseIcon } from 'lucide-react'
import { Progress } from '../../../components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'

interface DomainPurchase {
  id: number
  domain_name: string
  activation_status: 'not_activated' | 'configuring_dns' | 'activating_certificate' | 'active' | 'failed'
}

interface Project {
  id: number
  name: string
  domain_purchase?: DomainPurchase
}

interface InstanceBackup {
  id: number
  status: string
  trigger: string
  s3_path: string | null
  size_bytes: number | null
  error: string | null
  created_at: string
  manifest: Record<string, { size: number; s3_key: string }> | null
}

interface Deployment {
  id: number
  status: string
  deploy_type: string
  git_ref: string | null
  error_log: string | null
  completed_phases: string[]
  ecs_private_ip: string | null
  started_at: string | null
  completed_at: string | null
  created_at: string
}

interface SetupProgress {
  step: string
  message: string
  bootstrap_pid?: string
  started_at?: string
  log_content?: string
}

interface DevInstance {
  id: number
  name: string
  status: string
  runtime_state: string
  production_status: string
  ip_address: string
  source_repo_name: string
  deployment_url: string
  production_url: string
  rails_app_password: string
  rails_app_admin_email: string | null
  created_at: string
  setup_started_at: string
  setup_completed_at: string
  setup_error_log: string
  production_error_log: string
  setup_progress: string | null
  infrastructure_type: string
  size: string
  stopped_at: string | null
  monthly_cost_running: number | null
  monthly_cost_stopped: number | null
  monthly_cost_savings: number | null
  cost_savings_percentage: number | null
  github_repo_url: string | null
  sandbox_url: string | null
  ec2_console_url: string | null
  pilot_version_display: string
  pilot_branch: string | null
  project_type: 'rails_app' | 'django_app' | 'managed_agent'
  capabilities: {
    app_deployment: boolean
    starter_base_actions: boolean
  }
  target_pilot_version: string | null
  starter_base_version: string | null
  starter_base_updated_at: string | null
  target_starter_base_version: string | null
  upstream_remote_url: string | null
  cayu_pilot_bearer_token: string | null
  fly_ipv4: string[]
  fly_ipv6: string[]
  fly_ips_updated_at: string | null
  has_fly_ips?: boolean
  fly_ips_stale?: boolean
  is_demo: boolean
  project?: Project
  backups?: InstanceBackup[]
  deployments?: Deployment[]
  has_backup?: boolean
}

interface HealthCheck {
  id: number
  status: string
  memory_percent_used: number | null
  cpu_load_percent: number | null
  disk_percent_used: number | null
  pilot_reachable: boolean | null
  pilot_response_ms: number | null
  ec2_state: string | null
  ec2_system_reachable?: boolean | null
  ec2_instance_reachable?: boolean | null
  error_category: string | null
  error_message: string | null
  action_taken: string | null
  created_at: string
}

interface HealthCheckData {
  latest: HealthCheck | null
  history: HealthCheck[]
}

interface AdminDevInstanceShowProps {
  dev_instance: DevInstance
  health_checks: HealthCheckData
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
}

function healthStatusVariant(status: string): 'success' | 'warning' | 'destructive' | 'secondary' {
  const map: Record<string, 'success' | 'warning' | 'destructive' | 'secondary'> = {
    healthy: 'success', degraded: 'warning', unhealthy: 'destructive', unreachable: 'secondary',
  }
  return map[status] ?? 'secondary'
}

function formatHealthTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000)
  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`
  return `${Math.floor(diffSec / 86400)}d ago`
}

function ResourceGauge({ label, value }: { label: string; value: number | null }) {
  const pct = value ?? 0
  const colorClass = pct > 90 ? '[&>div]:bg-red-500' : pct > 70 ? '[&>div]:bg-amber-500' : '[&>div]:bg-green-500'
  const textColor = pct > 90 ? 'text-red-600 dark:text-red-400' : pct > 70 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className={`font-medium font-mono ${value != null ? textColor : 'text-muted-foreground'}`}>
          {value != null ? `${value.toFixed(1)}%` : '-'}
        </span>
      </div>
      <Progress value={pct} className={`h-2 ${colorClass}`} />
    </div>
  )
}

function Show({ dev_instance, health_checks }: AdminDevInstanceShowProps) {
  const { t, i18n } = useTranslation('admin')
  const [targetVersion, setTargetVersion] = useState('')
  const [updating, setUpdating] = useState(false)
  const [restarting, setRestarting] = useState(false)
  const [clearingSession, setClearingSession] = useState(false)
  const [refreshingIps, setRefreshingIps] = useState(false)
  const [availableTags, setAvailableTags] = useState<string[]>([])
  const [loadingTags, setLoadingTags] = useState(false)
  const [selectedVersion, setSelectedVersion] = useState('')
  const [resizeType, setResizeType] = useState(dev_instance.size)
  const supportsStarterBaseActions = dev_instance.capabilities.starter_base_actions
  const supportsAppDeployment = dev_instance.capabilities.app_deployment

  // Fetch available tags when upstream is configured
  useEffect(() => {
    if (dev_instance.upstream_remote_url && supportsStarterBaseActions) {
      setLoadingTags(true)
      fetch(`/admin/dev_instances/${dev_instance.id}/list_tags`)
        .then(res => res.json())
        .then(data => {
          if (data.tags) {
            setAvailableTags(data.tags)
          }
        })
        .catch(err => console.error('Failed to fetch tags:', err))
        .finally(() => setLoadingTags(false))
    }
  }, [dev_instance.upstream_remote_url, supportsStarterBaseActions, dev_instance.id])

  // Auto-refresh every 10 seconds if setup is in progress
  useEffect(() => {
    if (dev_instance.status === 'setup_in_progress' || dev_instance.status === 'provisioning') {
      const interval = setInterval(() => {
        router.reload({ only: ['dev_instance'] })
      }, 10000) // 10 seconds

      return () => clearInterval(interval)
    }
  }, [dev_instance.status])

  // Auto-refresh every 5 seconds if instance is starting or stopping
  useEffect(() => {
    if (dev_instance.runtime_state === 'starting' || dev_instance.runtime_state === 'stopping') {
      const interval = setInterval(() => {
        router.reload({ only: ['dev_instance'] })
      }, 5000) // 5 seconds - faster polling for quick operations

      return () => clearInterval(interval)
    }
  }, [dev_instance.runtime_state])

  // Parse setup progress
  let setupProgress: SetupProgress | null = null
  try {
    if (dev_instance.setup_progress) {
      setupProgress = JSON.parse(dev_instance.setup_progress)
    }
  } catch (e) {
    console.error('Failed to parse setup_progress:', e)
  }

  const getStatusBadge = (status: string) => {
    const statusVariants: Record<string, "success" | "warning" | "info" | "destructive" | "secondary"> = {
      'active': 'success',
      'setup_in_progress': 'warning',
      'provisioning': 'info',
      'failed': 'destructive',
      'terminated': 'secondary',
      'deploying': 'info',
      'deployed': 'success',
      'not_deployed': 'secondary',
      'stopped': 'secondary',
      'archived': 'secondary',
    }

    return (
      <Badge variant={statusVariants[status] || 'secondary'}>
        {t(`devInstances.status.${status}`, { defaultValue: status.replace('_', ' ') })}
      </Badge>
    )
  }

  const getRuntimeStateBadge = (runtimeState: string) => {
    const runtimeStateVariants: Record<string, "success" | "secondary" | "warning"> = {
      'running': 'success',
      'stopped': 'secondary',
      'starting': 'warning',
      'stopping': 'warning',
    }

    return (
      <Badge variant={runtimeStateVariants[runtimeState] || 'secondary'}>
        {t(`devInstances.runtimeState.${runtimeState}`, { defaultValue: runtimeState })}
      </Badge>
    )
  }

  const getInfrastructureLabel = (type: string) => {
    return t(`devInstances.provider.${type}`, { defaultValue: 'AWS EC2' })
  }

  const getBackupStatusBadge = (status: string) => {
    const statusVariants: Record<string, "success" | "warning" | "info" | "destructive" | "secondary"> = {
      'completed': 'success',
      'in_progress': 'info',
      'pending': 'warning',
      'failed': 'destructive',
    }

    return (
      <Badge variant={statusVariants[status] || 'secondary'}>
        {t(`devInstances.show.backups.statuses.${status}`, { defaultValue: status.replace('_', ' ') })}
      </Badge>
    )
  }

  const getDeploymentStatusBadge = (status: string) => {
    const statusVariants: Record<string, "success" | "warning" | "info" | "destructive" | "secondary"> = {
      'deployed': 'success',
      'building': 'info',
      'deploying': 'info',
      'pending': 'warning',
      'failed': 'destructive',
    }

    return (
      <Badge variant={statusVariants[status] || 'secondary'}>
        {t(`deployments.statuses.${status}`, { defaultValue: status.replace('_', ' ') })}
      </Badge>
    )
  }

  const formatDuration = (startedAt: string | null, completedAt: string | null) => {
    if (!startedAt || !completedAt) return '-'
    const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime()
    const seconds = Math.floor(ms / 1000)
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }

  const formatBytes = (bytes: number | null) => {
    if (!bytes) return 'Unknown'
    const units = ['B', 'KB', 'MB', 'GB']
    let value = bytes
    let unitIndex = 0
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024
      unitIndex++
    }
    return `${value.toFixed(1)} ${units[unitIndex]}`
  }

  const handleUpdatePilot = () => {
    if (!targetVersion) {
      alert(t('devInstances.show.starterBase.selectVersion'))
      return
    }

    if (!confirm(t('devInstances.show.cayuPilot.updateConfirm', { version: targetVersion }))) {
      return
    }

    setUpdating(true)

    router.post(`/admin/dev_instances/${dev_instance.id}/update_pilot`, {
      target_version: targetVersion
    }, {
      onFinish: () => setUpdating(false)
    })
  }

  const handleRestartCayuPilot = () => {
    if (!confirm(t('devInstances.show.cayuPilot.restartConfirm'))) {
      return
    }

    setRestarting(true)

    router.post(`/admin/dev_instances/${dev_instance.id}/restart_cayu_pilot`, {}, {
      onFinish: () => setRestarting(false)
    })
  }

  const handleClearSessionContext = () => {
    if (!confirm(t('devInstances.show.cayuPilot.clearSessionConfirm'))) {
      return
    }

    setClearingSession(true)

    router.post(`/admin/dev_instances/${dev_instance.id}/clear_session_context`, {}, {
      onFinish: () => setClearingSession(false)
    })
  }

  const handleRefreshFlyIps = () => {
    setRefreshingIps(true)

    router.post(`/admin/dev_instances/${dev_instance.id}/refresh_fly_ips`, {}, {
      onFinish: () => setRefreshingIps(false)
    })
  }

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('devInstances.index.title'), href: '/admin/dev_instances' }, { label: t('devInstances.show.title') }]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{dev_instance.name}</h1>
            {dev_instance.is_demo && (
              <Badge className="mt-2 bg-blue-500 hover:bg-blue-600 text-white">
                {t('devInstances.show.templateDemo')}
              </Badge>
            )}
            <p className="mt-2 text-sm text-muted-foreground">
              {t('devInstances.show.description')}
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link href="/admin/dev_instances">{t('devInstances.show.backButton')}</Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Instance Information */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('devInstances.show.info.title')}</CardTitle>
                <CardDescription>{t('devInstances.show.info.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.info.name')}</p>
                    <p className="mt-1 text-sm text-foreground">{dev_instance.name}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.info.provider')}</p>
                    <p className="mt-1 text-sm text-foreground">{getInfrastructureLabel(dev_instance.infrastructure_type)}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.info.status')}</p>
                    <div className="mt-1">
                      {getStatusBadge(dev_instance.status)}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.info.runtimeState')}</p>
                    <div className="mt-1">
                      {getRuntimeStateBadge(dev_instance.runtime_state)}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.quickInfo.instanceType')}</p>
                    {dev_instance.runtime_state === 'stopped' ? (
                      <div className="mt-1 flex items-center gap-2">
                        <Select value={resizeType} onValueChange={setResizeType}>
                          <SelectTrigger className="h-8 text-sm w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="t4g.small">t4g.small (2 GB)</SelectItem>
                            <SelectItem value="t4g.medium">t4g.medium (4 GB)</SelectItem>
                            <SelectItem value="t4g.large">t4g.large (8 GB)</SelectItem>
                            <SelectItem value="t4g.xlarge">t4g.xlarge (16 GB)</SelectItem>
                          </SelectContent>
                        </Select>
                        {resizeType !== dev_instance.size && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs"
                            onClick={() => router.post(`/admin/dev_instances/${dev_instance.id}/resize`, { instance_type: resizeType })}
                          >
                            Resize
                          </Button>
                        )}
                      </div>
                    ) : (
                      <p className="mt-1 text-sm text-foreground">{dev_instance.size}</p>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.info.ipAddress')}</p>
                    <p className="mt-1 text-sm text-foreground font-mono">
                      {dev_instance.ip_address || t('devInstances.show.urls.notConfigured')}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.quickInfo.sourceRepo')}</p>
                    <a
                      href={`https://github.com/cayu-dev/${dev_instance.source_repo_name}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 text-sm text-blue-600 hover:text-blue-900 font-mono"
                    >
                      cayu-dev/{dev_instance.source_repo_name}
                    </a>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.info.project')}</p>
                    {dev_instance.project ? (
                      <Link href={`/admin/projects/${dev_instance.project.id}`} className="mt-1 text-sm text-blue-600 hover:text-blue-900">
                        {dev_instance.project.name}
                      </Link>
                    ) : (
                      <p className="mt-1 text-sm text-muted-foreground">{t('devInstances.show.info.noProject')}</p>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.info.created')}</p>
                    <p className="mt-1 text-sm text-foreground">
                      {new Date(dev_instance.created_at).toLocaleDateString(getDateLocale(), {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>

                  {dev_instance.github_repo_url && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.info.githubRepo')}</p>
                      <a
                        href={dev_instance.github_repo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 text-sm text-blue-600 hover:text-blue-900 font-mono break-all"
                      >
                        {dev_instance.github_repo_url.replace('https://github.com/', '')}
                      </a>
                    </div>
                  )}

                  {dev_instance.ec2_console_url && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.info.ec2Console')}</p>
                      <a
                        href={dev_instance.ec2_console_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 text-sm text-blue-600 hover:text-blue-900"
                      >
                        {t('devInstances.show.info.viewInAwsConsole')}
                      </a>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Cayu-Pilot Version */}
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('devInstances.show.cayuPilot.title')}</CardTitle>
                <CardDescription>{t('devInstances.show.cayuPilot.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-6">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.cayuPilot.version')}</p>
                    <p className="mt-1 text-lg font-semibold text-foreground font-mono">
                      {dev_instance.pilot_version_display}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.cayuPilot.targetVersion')}</p>
                    <p className={`mt-1 text-lg font-semibold font-mono ${dev_instance.target_pilot_version && dev_instance.target_pilot_version !== dev_instance.pilot_version_display ? 'text-amber-600' : 'text-muted-foreground'}`}>
                      {dev_instance.target_pilot_version || '—'}
                    </p>
                  </div>
                  {dev_instance.pilot_branch && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.cayuPilot.branch')}</p>
                      <p className="mt-1 text-lg font-semibold text-foreground font-mono">
                        {dev_instance.pilot_branch}
                      </p>
                    </div>
                  )}
                </div>

                <div className="border-t border-border pt-4">
                  <Label htmlFor="target_version" className="text-sm font-medium">
                    {t('devInstances.show.cayuPilot.updateButton', { version: '' }).replace('{{version}}', '')}
                  </Label>
                  <div className="mt-2 flex gap-2">
                    <Input
                      id="target_version"
                      type="text"
                      value={targetVersion}
                      onChange={(e) => setTargetVersion(e.target.value)}
                      placeholder="v1.3.0"
                      className="font-mono"
                      disabled={updating}
                    />
                    <Button
                      onClick={handleUpdatePilot}
                      disabled={updating || !targetVersion}
                    >
                      {updating ? t('devInstances.show.cayuPilot.updatingButton') : t('devInstances.show.dialogs.updatePilot.confirm')}
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t('devInstances.show.cayuPilot.updateHint')}
                  </p>
                </div>

                <div className="border-t border-border pt-4">
                  <Label className="text-sm font-medium">
                    {t('devInstances.show.cayuPilot.restartTitle')}
                  </Label>
                  <p className="mt-1 text-xs text-muted-foreground mb-2">
                    {t('devInstances.show.cayuPilot.restartDescription')}
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleRestartCayuPilot}
                    disabled={restarting || dev_instance.runtime_state !== 'running'}
                  >
                    {restarting ? t('devInstances.show.cayuPilot.restartingButton') : t('devInstances.show.cayuPilot.restartButton')}
                  </Button>
                </div>

                <div className="border-t border-border pt-4">
                  <Label className="text-sm font-medium">
                    {t('devInstances.show.cayuPilot.clearSessionTitle')}
                  </Label>
                  <p className="mt-1 text-xs text-muted-foreground mb-2">
                    {t('devInstances.show.cayuPilot.clearSessionDescription')}
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleClearSessionContext}
                    disabled={clearingSession || dev_instance.runtime_state !== 'running'}
                  >
                    {clearingSession ? t('devInstances.show.cayuPilot.clearingButton') : t('devInstances.show.cayuPilot.clearSessionButton')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Upstream Sync Status */}
            {dev_instance.status === 'active' && supportsStarterBaseActions && (
              <Card className="shadow-none">
                <CardHeader>
                  <CardTitle>{t('devInstances.show.starterBase.title')}</CardTitle>
                  <CardDescription>{t('devInstances.show.starterBase.description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.starterBase.version')}</p>
                      <div className="mt-1">
                        {dev_instance.starter_base_version ? (
                          <Badge variant="secondary" className="font-mono">
                            {dev_instance.starter_base_version}
                          </Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">{t('devInstances.show.starterBase.notSet')}</span>
                        )}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.starterBase.lastUpdated')}</p>
                      <p className="mt-1 text-sm text-foreground">
                        {dev_instance.starter_base_updated_at
                          ? new Date(dev_instance.starter_base_updated_at).toLocaleString()
                          : t('devInstances.show.starterBase.never')}
                      </p>
                    </div>
                  </div>

                  {dev_instance.target_starter_base_version &&
                    dev_instance.target_starter_base_version !== dev_instance.starter_base_version && (
                    <div className="border-t border-border pt-4">
                      <div className="flex items-center justify-between gap-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-3 py-2">
                        <span className="text-sm text-amber-800 dark:text-amber-200">
                          Pending update: <span className="font-mono">{dev_instance.starter_base_version || 'unversioned'}</span> → <span className="font-mono font-semibold">{dev_instance.target_starter_base_version}</span>
                        </span>
                        <Button
                          size="sm"
                          onClick={() => {
                            if (confirm(`Trigger starter-base sync to ${dev_instance.target_starter_base_version}? This will run as a workflow on the instance.`)) {
                              router.post(`/admin/dev_instances/${dev_instance.id}/sync_starter_base`)
                            }
                          }}
                          disabled={dev_instance.runtime_state !== 'running'}
                        >
                          Sync Now
                        </Button>
                      </div>
                      {dev_instance.runtime_state !== 'running' && (
                        <p className="mt-1 text-xs text-muted-foreground">Instance must be running to sync</p>
                      )}
                    </div>
                  )}

                  {dev_instance.upstream_remote_url ? (
                    <div className="border-t border-border pt-4">
                      <p className="text-sm font-medium text-muted-foreground mb-2">{t('devInstances.show.starterBase.updateToVersion')}</p>
                      <div className="flex gap-2">
                        <Select
                          value={selectedVersion}
                          onValueChange={setSelectedVersion}
                          disabled={dev_instance.runtime_state !== 'running' || loadingTags}
                        >
                          <SelectTrigger className="flex-1 font-mono">
                            <SelectValue placeholder={loadingTags ? t('devInstances.show.starterBase.loadingVersions') : t('devInstances.show.starterBase.selectVersion')} />
                          </SelectTrigger>
                          <SelectContent>
                            {availableTags.length > 0 ? (
                              availableTags.map((tag) => (
                                <SelectItem key={tag} value={tag} className="font-mono">
                                  {tag}
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value="_none" disabled>
                                {loadingTags ? t('devInstances.show.starterBase.loading') : t('devInstances.show.starterBase.noVersions')}
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <Button
                          onClick={() => {
                            if (!selectedVersion) {
                              alert(t('devInstances.show.starterBase.selectVersion'))
                              return
                            }
                            if (confirm(t('devInstances.show.starterBase.updateVersionConfirm', { version: selectedVersion }))) {
                              router.post(`/admin/dev_instances/${dev_instance.id}/update_starter_base`, {
                                version: selectedVersion
                              })
                            }
                          }}
                          disabled={!selectedVersion || dev_instance.runtime_state !== 'running'}
                        >
                          {t('devInstances.show.starterBase.updateButton')}
                        </Button>
                      </div>
                      {dev_instance.runtime_state !== 'running' && (
                        <p className="mt-2 text-xs text-muted-foreground">
                          {t('devInstances.show.starterBase.mustBeRunning')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="border-t border-border pt-4">
                      <p className="text-sm font-medium text-muted-foreground mb-2">{t('devInstances.show.starterBase.updateToVersion')}</p>
                      <div className="text-sm text-muted-foreground bg-muted/50 border border-border rounded p-3">
                        {t('devInstances.show.starterBase.configureUpstreamFirst')}
                      </div>
                    </div>
                  )}

                  <div className="border-t border-border pt-4">
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.starterBase.upstreamRepo')}</p>
                    <p className="mt-1 text-sm text-foreground font-mono">
                      {dev_instance.upstream_remote_url
                        ? dev_instance.upstream_remote_url.replace(/https:\/\/[^@]+@github\.com\//, '').replace('.git', '')
                        : t('devInstances.show.starterBase.notConfigured')}
                    </p>
                    {!dev_instance.upstream_remote_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => router.post(`/admin/dev_instances/${dev_instance.id}/configure_upstream`)}
                        disabled={dev_instance.runtime_state !== 'running'}
                      >
                        {t('devInstances.show.starterBase.configureUpstream')}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* URLs */}
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('devInstances.show.urls.title')}</CardTitle>
                <CardDescription>{t('devInstances.show.urls.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.urls.sandboxUrl')}</p>
                  {dev_instance.sandbox_url || dev_instance.deployment_url ? (
                    <a
                      href={dev_instance.sandbox_url || dev_instance.deployment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 text-sm text-blue-600 hover:text-blue-900 font-mono break-all"
                    >
                      {dev_instance.sandbox_url || dev_instance.deployment_url}
                    </a>
                  ) : (
                    <p className="mt-1 text-sm text-muted-foreground">{t('devInstances.show.urls.notAvailable')}</p>
                  )}
                </div>

                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.urls.liveUrl')}</p>
                  {dev_instance.production_url ? (
                    <a
                      href={dev_instance.production_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 text-sm text-blue-600 hover:text-blue-900 font-mono break-all"
                    >
                      {dev_instance.production_url}
                    </a>
                  ) : (
                    <p className="mt-1 text-sm text-muted-foreground">{t('devInstances.show.urls.notDeployed')}</p>
                  )}
                </div>

                {dev_instance.project?.domain_purchase && (
                  <div className="border-t border-border pt-4">
                    <p className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                      <GlobeIcon className="h-4 w-4" />
                      {t('devInstances.show.urls.customDomain')}
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      <a
                        href={`https://${dev_instance.project.domain_purchase.domain_name}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-900 font-mono"
                      >
                        {dev_instance.project.domain_purchase.domain_name}
                      </a>
                      <Badge variant={
                        dev_instance.project.domain_purchase.activation_status === 'active' ? 'success' :
                        dev_instance.project.domain_purchase.activation_status === 'failed' ? 'destructive' :
                        dev_instance.project.domain_purchase.activation_status === 'configuring_dns' || dev_instance.project.domain_purchase.activation_status === 'activating_certificate' ? 'info' : 'secondary'
                      }>
                        {t(`devInstances.show.urls.domainStatuses.${dev_instance.project.domain_purchase.activation_status}`)}
                      </Badge>
                    </div>
                    <Link
                      href={`/admin/projects/${dev_instance.project.id}`}
                      className="mt-2 inline-block text-xs text-muted-foreground hover:text-foreground"
                    >
                      {t('devInstances.show.urls.viewDomainDetails')} →
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Instance Health */}
            <Card className="shadow-none">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <HeartPulseIcon className="h-5 w-5 text-muted-foreground" />
                    <CardTitle>{t('healthChecks.show.title')}</CardTitle>
                  </div>
                  {health_checks.latest && (
                    <div className="flex items-center gap-2">
                      <Badge variant={healthStatusVariant(health_checks.latest.status)}>
                        {t(`healthChecks.status.${health_checks.latest.status}`)}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatHealthTime(health_checks.latest.created_at)}
                      </span>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!health_checks.latest ? (
                  <p className="text-sm text-muted-foreground">{t('healthChecks.show.noData')}</p>
                ) : (
                  <div className="space-y-4">
                    {/* Resource Gauges */}
                    <div className="grid grid-cols-3 gap-4">
                      <ResourceGauge label={t('healthChecks.table.memory')} value={health_checks.latest.memory_percent_used} />
                      <ResourceGauge label={t('healthChecks.table.cpu')} value={health_checks.latest.cpu_load_percent} />
                      <ResourceGauge label={t('healthChecks.table.disk')} value={health_checks.latest.disk_percent_used} />
                    </div>

                    {/* Pilot & EC2 Status */}
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-block h-2 w-2 rounded-full ${health_checks.latest.pilot_reachable ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-muted-foreground">
                          {health_checks.latest.pilot_reachable ? t('healthChecks.show.pilotReachable') : t('healthChecks.show.pilotUnreachable')}
                        </span>
                        {health_checks.latest.pilot_response_ms != null && (
                          <span className="text-xs font-mono text-muted-foreground">
                            {t('healthChecks.show.responseTime', { ms: health_checks.latest.pilot_response_ms })}
                          </span>
                        )}
                      </div>
                      {health_checks.latest.ec2_state && (
                        <span className="text-xs text-muted-foreground">
                          {t('healthChecks.show.ec2State', { state: health_checks.latest.ec2_state })}
                        </span>
                      )}
                    </div>

                    {/* Error Banner */}
                    {health_checks.latest.error_message && (
                      <div className="rounded-md border border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20 p-3">
                        <p className="text-sm font-medium text-red-800 dark:text-red-300">{health_checks.latest.error_category}</p>
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">{health_checks.latest.error_message}</p>
                      </div>
                    )}

                    {/* History Table */}
                    {health_checks.history.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-2">{t('healthChecks.show.history')}</p>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-muted">
                              <tr>
                                <th className="px-2 py-1 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.lastChecked')}</th>
                                <th className="px-2 py-1 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.status')}</th>
                                <th className="px-2 py-1 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.memory')}</th>
                                <th className="px-2 py-1 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.cpu')}</th>
                                <th className="px-2 py-1 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.disk')}</th>
                                <th className="px-2 py-1 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.pilotMs')}</th>
                                <th className="px-2 py-1 text-left text-xs font-medium text-muted-foreground">{t('healthChecks.table.action')}</th>
                              </tr>
                            </thead>
                            <tbody className="bg-card divide-y divide-border">
                              {health_checks.history.map(hc => (
                                <tr key={hc.id} className="hover:bg-muted transition-colors">
                                  <td className="px-2 py-1 text-xs text-muted-foreground whitespace-nowrap">{formatHealthTime(hc.created_at)}</td>
                                  <td className="px-2 py-1">
                                    <Badge variant={healthStatusVariant(hc.status)} className="text-[10px] px-1 py-0">{hc.status}</Badge>
                                  </td>
                                  <td className="px-2 py-1 text-xs font-mono">{hc.memory_percent_used != null ? `${hc.memory_percent_used.toFixed(1)}%` : '-'}</td>
                                  <td className="px-2 py-1 text-xs font-mono">{hc.cpu_load_percent != null ? `${hc.cpu_load_percent.toFixed(1)}%` : '-'}</td>
                                  <td className="px-2 py-1 text-xs font-mono">{hc.disk_percent_used != null ? `${hc.disk_percent_used.toFixed(1)}%` : '-'}</td>
                                  <td className="px-2 py-1 text-xs font-mono">{hc.pilot_response_ms ?? '-'}</td>
                                  <td className="px-2 py-1 text-xs">{hc.action_taken || ''}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Fly.io IP Addresses */}
            {dev_instance.production_status === 'deployed' && (
              <Card className="shadow-none">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{t('devInstances.show.flyIps.title')}</CardTitle>
                      <CardDescription>{t('devInstances.show.flyIps.description')}</CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefreshFlyIps}
                      disabled={refreshingIps}
                    >
                      {refreshingIps ? t('devInstances.show.flyIps.refreshing') : t('devInstances.show.flyIps.refresh')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.flyIps.ipv4')}</p>
                    <p className="mt-1 text-sm text-foreground font-mono">
                      {dev_instance.fly_ipv4?.length ? dev_instance.fly_ipv4.join(', ') : '-'}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.flyIps.ipv6')}</p>
                    <p className="mt-1 text-sm text-foreground font-mono break-all">
                      {dev_instance.fly_ipv6?.length ? dev_instance.fly_ipv6.join(', ') : '-'}
                    </p>
                  </div>

                  {dev_instance.fly_ips_updated_at && (
                    <div className="text-xs text-muted-foreground">
                      {t('devInstances.show.flyIps.lastUpdated')}: {new Date(dev_instance.fly_ips_updated_at).toLocaleString(i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'es' ? 'es-ES' : 'en-US')}
                      {dev_instance.fly_ips_stale && (
                        <span className="ml-2 text-warning">{t('devInstances.show.flyIps.stale')}</span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Admin Credentials */}
            {dev_instance.rails_app_password && (
              <Card className="shadow-none border-warning/30 bg-warning/10">
                <CardHeader>
                  <CardTitle>{t('devInstances.show.credentials.title')}</CardTitle>
                  <CardDescription>{t('devInstances.show.credentials.description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">{t('devInstances.show.credentials.email')}</p>
                    <p className="mt-1 text-sm text-foreground font-mono">{dev_instance.rails_app_admin_email || 'admin@example.com'}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-foreground">{t('devInstances.show.credentials.password')}</p>
                    <p className="mt-1 text-sm text-foreground font-mono">{dev_instance.rails_app_password}</p>
                  </div>

                  <div className="text-xs text-warning-foreground bg-warning/20 border border-warning/40 rounded p-2">
                    {t('devInstances.show.credentials.securityNote')}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Setup Progress - Live Updates */}
            {setupProgress && (dev_instance.status === 'setup_in_progress' || dev_instance.status === 'provisioning') && (
              <Card className="shadow-none border-blue-500/20 bg-blue-500/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse"></div>
                    {t('devInstances.show.setupProgress.title')}
                  </CardTitle>
                  <CardDescription>
                    {t('devInstances.show.setupProgress.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.setupInfo.currentStep')}</p>
                    <p className="mt-1 text-lg font-semibold text-blue-600 dark:text-blue-400">
                      {setupProgress.step?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                  </div>

                  {setupProgress.message && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.setupInfo.statusMessage')}</p>
                      <p className="mt-1 text-sm text-foreground">{setupProgress.message}</p>
                    </div>
                  )}

                  {setupProgress.started_at && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.setupInfo.startedAt')}</p>
                      <p className="mt-1 text-sm text-foreground">
                        {new Date(setupProgress.started_at).toLocaleString()}
                      </p>
                    </div>
                  )}

                  {setupProgress.bootstrap_pid && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.setupInfo.processId')}</p>
                      <p className="mt-1 text-sm text-foreground font-mono">{setupProgress.bootstrap_pid}</p>
                    </div>
                  )}

                  {setupProgress.log_content && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">{t('devInstances.show.setupInfo.bootstrapLogs')}</p>
                      <pre className="text-xs bg-muted border border-border rounded p-3 overflow-x-auto max-h-96 overflow-y-auto font-mono">
                        {setupProgress.log_content}
                      </pre>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-xs text-muted-foreground italic border-t border-border pt-3 mt-2">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {t('devInstances.show.setupInfo.autoRefreshing')}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Setup Information */}
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('devInstances.show.setupInfo.title')}</CardTitle>
                <CardDescription>{t('devInstances.show.setupInfo.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.setupInfo.liveStatus')}</p>
                    <div className="mt-1">
                      {getStatusBadge(dev_instance.production_status)}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.setupInfo.setupStarted')}</p>
                    <p className="mt-1 text-sm text-foreground">
                      {dev_instance.setup_started_at ? new Date(dev_instance.setup_started_at).toLocaleString() : '-'}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.setupInfo.setupCompleted')}</p>
                    <p className="mt-1 text-sm text-foreground">
                      {dev_instance.setup_completed_at ? new Date(dev_instance.setup_completed_at).toLocaleString() : '-'}
                    </p>
                  </div>
                </div>

                {/* Error Logs */}
                {dev_instance.setup_error_log && (
                  <div>
                    <p className="text-sm font-medium text-destructive mb-2">{t('devInstances.show.setupInfo.setupErrorLog')}</p>
                    <pre className="text-xs bg-destructive/10 border border-destructive/30 rounded p-3 overflow-x-auto max-h-60 overflow-y-auto">
                      {dev_instance.setup_error_log}
                    </pre>
                  </div>
                )}

                {dev_instance.production_error_log && (
                  <div>
                    <p className="text-sm font-medium text-destructive mb-2">{t('devInstances.show.setupInfo.liveErrorLog')}</p>
                    <pre className="text-xs bg-destructive/10 border border-destructive/30 rounded p-3 overflow-x-auto max-h-60 overflow-y-auto">
                      {dev_instance.production_error_log}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Instance Backups - only show if backups exist */}
            {dev_instance.backups && dev_instance.backups.length > 0 && (() => {
              // Sort backups by created_at descending (newest first)
              const sortedBackups = [...dev_instance.backups].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
              )
              const latestBackup = sortedBackups[0]
              return (
                <Card className="shadow-none">
                  <CardHeader>
                    <CardTitle>{t('devInstances.show.backups.title')}</CardTitle>
                    <CardDescription>{t('devInstances.show.backups.description')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Latest Backup Summary */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.backups.status')}</p>
                        <div className="mt-1">
                          {getBackupStatusBadge(latestBackup.status)}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.backups.size')}</p>
                        <p className="mt-1 text-sm text-foreground">
                          {formatBytes(latestBackup.size_bytes)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.backups.created')}</p>
                        <p className="mt-1 text-sm text-foreground">
                          {new Date(latestBackup.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.backups.trigger')}</p>
                        <p className="mt-1 text-sm text-foreground capitalize">
                          {t(`devInstances.show.backups.triggers.${latestBackup.trigger}`, { defaultValue: latestBackup.trigger })}
                        </p>
                      </div>
                    </div>

                    {/* S3 Path */}
                    {latestBackup.s3_path && (
                      <div className="border-t border-border pt-4">
                        <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.backups.s3Path')}</p>
                        <p className="mt-1 text-xs text-foreground font-mono break-all">
                          s3://cayu-instance-backups/{latestBackup.s3_path}
                        </p>
                      </div>
                    )}

                    {/* Error if failed */}
                    {latestBackup.status === 'failed' && latestBackup.error && (
                      <div className="border-t border-border pt-4">
                        <p className="text-sm font-medium text-destructive mb-2">{t('devInstances.show.backups.backupError')}</p>
                        <pre className="text-xs bg-destructive/10 border border-destructive/30 rounded p-3 overflow-x-auto">
                          {latestBackup.error}
                        </pre>
                      </div>
                    )}

                    {/* Manifest */}
                    {latestBackup.manifest && Object.keys(latestBackup.manifest).length > 0 && (
                      <div className="border-t border-border pt-4">
                        <p className="text-sm font-medium text-muted-foreground mb-2">{t('devInstances.show.backups.backupContents')}</p>
                        <div className="space-y-1">
                          {Object.entries(latestBackup.manifest)
                            .filter(([, info]) => info && typeof info === 'object' && info.size != null)
                            .map(([name, info]) => (
                              <div key={name} className="flex justify-between text-xs">
                                <span className="text-muted-foreground">{name}</span>
                                <span className="font-mono">{formatBytes(info.size)}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* All Backups History */}
                    {sortedBackups.length > 1 && (
                      <div className="border-t border-border pt-4">
                        <p className="text-sm font-medium text-muted-foreground mb-2">{t('devInstances.show.backups.backupHistory')}</p>
                        <div className="space-y-2">
                          {sortedBackups.slice(1).map((backup) => (
                            <div key={backup.id} className="flex items-center justify-between text-sm border-b border-border pb-2 last:border-0">
                              <div className="flex items-center gap-2">
                                {getBackupStatusBadge(backup.status)}
                                <span className="text-muted-foreground">
                                  {new Date(backup.created_at).toLocaleString()}
                                </span>
                              </div>
                              <span className="text-xs font-mono">
                                {formatBytes(backup.size_bytes)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            })()}

            {/* Deployment History */}
            {dev_instance.deployments && dev_instance.deployments.length > 0 && (() => {
              const sortedDeployments = [...dev_instance.deployments].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
              )
              const latestDeployment = sortedDeployments[0]
              return (
                <Card className="shadow-none">
                  <CardHeader>
                    <CardTitle>{t('deployments.title')}</CardTitle>
                    <CardDescription>{t('deployments.description')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Latest Deployment Summary */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('deployments.status')}</p>
                        <div className="mt-1">
                          {getDeploymentStatusBadge(latestDeployment.status)}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('deployments.deployType')}</p>
                        <p className="mt-1 text-sm text-foreground font-mono">
                          {latestDeployment.deploy_type}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('deployments.gitRef')}</p>
                        <p className="mt-1 text-sm text-foreground font-mono">
                          {latestDeployment.git_ref || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('deployments.duration')}</p>
                        <p className="mt-1 text-sm text-foreground">
                          {formatDuration(latestDeployment.started_at, latestDeployment.completed_at)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('deployments.startedAt')}</p>
                        <p className="mt-1 text-sm text-foreground">
                          {latestDeployment.started_at ? new Date(latestDeployment.started_at).toLocaleString() : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('deployments.completedAt')}</p>
                        <p className="mt-1 text-sm text-foreground">
                          {latestDeployment.completed_at ? new Date(latestDeployment.completed_at).toLocaleString() : '-'}
                        </p>
                      </div>
                      {latestDeployment.ecs_private_ip && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">{t('deployments.ecsPrivateIp')}</p>
                          <p className="mt-1 text-sm text-foreground font-mono">
                            {latestDeployment.ecs_private_ip}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Error Log if latest is failed */}
                    {latestDeployment.status === 'failed' && latestDeployment.error_log && (
                      <div className="border-t border-border pt-4">
                        <p className="text-sm font-medium text-destructive mb-2">{t('deployments.errorLog')}</p>
                        <pre className="text-xs bg-destructive/10 border border-destructive/30 rounded p-3 overflow-x-auto max-h-60 overflow-y-auto">
                          {latestDeployment.error_log}
                        </pre>
                      </div>
                    )}

                    {/* Previous Deployments History */}
                    {sortedDeployments.length > 1 && (
                      <div className="border-t border-border pt-4">
                        <p className="text-sm font-medium text-muted-foreground mb-2">{t('deployments.previousDeployments')}</p>
                        <div className="space-y-2">
                          {sortedDeployments.slice(1).map((deployment) => (
                            <div key={deployment.id} className="flex items-center justify-between text-sm border-b border-border pb-2 last:border-0">
                              <div className="flex items-center gap-2">
                                {getDeploymentStatusBadge(deployment.status)}
                                <span className="text-muted-foreground">
                                  {new Date(deployment.created_at).toLocaleString()}
                                </span>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {formatDuration(deployment.started_at, deployment.completed_at)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            })()}
          </div>

          {/* Actions Sidebar */}
          <div className="space-y-6">
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('devInstances.show.actions.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dev_instance.project && (
                  <Button variant="outline" className="w-full justify-start" asChild>
                    <Link href={`/projects/${dev_instance.project.id}`}>{t('devInstances.show.info.viewProject')}</Link>
                  </Button>
                )}
                <Button variant="outline" className="w-full justify-start" asChild>
                  <Link href={`/admin/dev_instances/${dev_instance.id}/system_secrets`}>
                    {t('devInstances.show.actions.viewSystemSecrets')}
                  </Link>
                </Button>
                {dev_instance.project && (
                  <Button variant="outline" className="w-full justify-start" asChild>
                    <Link href={`/admin/dev_instances/${dev_instance.id}/app_secrets`}>
                      {t('devInstances.show.actions.viewAppSecrets')}
                    </Link>
                  </Button>
                )}
                {dev_instance.status === 'setup_failed' && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => router.post(`/admin/dev_instances/${dev_instance.id}/retry_bootstrap`)}
                  >
                    {t('devInstances.show.actions.runBootstrap')}
                  </Button>
                )}
                {/* Stop/Start Buttons */}
                {dev_instance.runtime_state === 'stopped' ? (
                  <Button
                    variant="default"
                    className="w-full justify-start bg-green-600 hover:bg-green-700"
                    onClick={() => router.post(`/admin/dev_instances/${dev_instance.id}/start`)}
                  >
                    {t('devInstances.show.actions.startInstance')}
                  </Button>
                ) : dev_instance.runtime_state === 'running' && (
                  <Button
                    variant="outline"
                    className="w-full justify-start border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-950"
                    onClick={() => router.post(`/admin/dev_instances/${dev_instance.id}/stop`)}
                  >
                    {t('devInstances.show.actions.stopInstance', { percentage: dev_instance.cost_savings_percentage })}
                  </Button>
                )}
                {supportsAppDeployment && (dev_instance.status === 'active' || dev_instance.status === 'setup_in_progress') && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => router.post(`/admin/dev_instances/${dev_instance.id}/deploy_to_production`)}
                  >
                    {t('devInstances.show.actions.deployToLive')}
                  </Button>
                )}
                {!dev_instance.project && (
                  <Button
                    variant="outline"
                    className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => {
                      if (confirm(t('devInstances.show.actions.deleteConfirm', { name: dev_instance.name }))) {
                        router.delete(`/admin/dev_instances/${dev_instance.id}`)
                      }
                    }}
                  >
                    {t('devInstances.show.actions.deleteInstance')}
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('devInstances.show.quickInfo.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {dev_instance.monthly_cost_running != null && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('devInstances.show.quickInfo.monthlyCost')}</p>
                    <p className="mt-1 text-sm text-foreground">
                      {t('devInstances.show.quickInfo.creditsPerMonth', { cost: Math.round(dev_instance.monthly_cost_running) })}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('users.show.statistics.accountAge')}</p>
                  <p className="mt-1 text-sm text-foreground">
                    {t('users.show.statistics.days', { count: Math.floor((Date.now() - new Date(dev_instance.created_at).getTime()) / (1000 * 60 * 60 * 24)) })}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}




Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
